import { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { useSetAtom } from 'jotai';

import { UserPreview, UserData } from '@bizzabo/entities';

import { getGuestProfileId, getHostProfileId, isHost } from '../../helpers';
import MeetingLinkBtn from '../MeetingLinkBtn';
import MeetingItem from '../MeetingItem';
import { ScheduledMeeting } from '../../types';
import useTypeTracking from '../../../../analytics';
import LocationDetails from '../LocationDetails';
import TimeInfo from '../TimeInfo';
import DateAndTime from '../DateAndTime';
import LocationBadge from '../LocationBadge';
import UserBadgeAction from '../UserBadgeAction';
import ItemDropdownMenu from '../ItemDropdownMenu';
import ItemDropdownMenuButton from '../ItemDropdownMenu/ItemDropdownMenuButton';
import AddToCalendarModal from '../../components/AddToCalendarModal';
import { joinVideoAtom } from '../../../../video/atoms';
import { useMeetings } from '../../MeetingsTool/MeetingsToolContext';
import CancelMeetingModal from '../CancelMeetingModal';

interface ScheduledItemProps {
    meeting: ScheduledMeeting;
    currentUser: UserData;
    user: UserPreview;
}

const ScheduledItem: React.FC<ScheduledItemProps> = ({ meeting, currentUser, user }) => {
    const { eventId } = useMeetings();
    const classes = useStyles();
    const isCurrentUserHost = isHost(meeting, currentUser.userProfileId);
    const [isAddToCalendarModalOpen, setIsAddToCalendarModalOpen] = useState(false);
    const [isCancelMeetingModalOpen, setIsCancelMeetingModalOpen] = useState(false);
    const setJoinVideo = useSetAtom(joinVideoAtom);
    const { Track, trackClickEvent } = useTypeTracking({
        data: {
            description: {
                meeting_id: meeting.id,
                location: 'MEETING_CARD',
                flow: 'REVIEW_SCHEDULED',
                meeting_participant_role: isCurrentUserHost ? 'HOST' : 'GUEST',
                meeting_type: 'SCHEDULED',
            },
        },
    });

    const onCancelMeeting = () => {
        setIsCancelMeetingModalOpen(true);
        trackClickEvent({
            subCategory: 'MEETING_CANCELLED_INITIATED',
        });
    };

    const onAddToCalendar = () => {
        setIsAddToCalendarModalOpen(true);
        trackClickEvent({
            subCategory: 'ADD_TO_CALENDAR',
        });
    };

    const closeCalendarModal = () => {
        setIsAddToCalendarModalOpen(false);
        trackClickEvent({
            subCategory: 'CLOSE_CALENDAR',
        });
    };

    const closeCancelMeetingModal = () => {
        setIsCancelMeetingModalOpen(false);
        trackClickEvent({
            subCategory: 'MEETING_CANCELLED_CLOSED',
        });
    };

    const meetingLinkClick = async () => {
        const hostProfileId = getHostProfileId(meeting);
        const guestProfileId = getGuestProfileId(meeting);
        setJoinVideo({
            eventId,
            meetingId: meeting.id,
            hostProfileId,
            guestProfileId,
        });
    };

    return (
        <Track>
            <MeetingItem>
                <TimeInfo>
                    <DateAndTime startDate={meeting.startDate} endDate={meeting.endDate} />
                    <LocationBadge locationType={meeting.locationType} />
                </TimeInfo>
                <UserBadgeAction user={user} hasNotification={!!meeting.notifications?.length}>
                    <ItemDropdownMenu>
                        {closeDropdown => (
                            <>
                                <ItemDropdownMenuButton
                                    onClick={() => {
                                        closeDropdown();
                                        onAddToCalendar();
                                    }}
                                >
                                    Add to calendar
                                </ItemDropdownMenuButton>
                                <ItemDropdownMenuButton
                                    className={classes.redColor}
                                    onClick={() => {
                                        closeDropdown();
                                        onCancelMeeting();
                                    }}
                                >
                                    Cancel meeting
                                </ItemDropdownMenuButton>
                            </>
                        )}
                    </ItemDropdownMenu>
                </UserBadgeAction>
                <LocationDetails physicalLocation={meeting.physicalLocation}>
                    <MeetingLinkBtn
                        meeting={meeting}
                        isHost={isCurrentUserHost}
                        meetingLinkClick={meetingLinkClick}
                    />
                </LocationDetails>
            </MeetingItem>

            <CancelMeetingModal
                isOpen={isCancelMeetingModalOpen}
                meeting={meeting}
                closeModal={closeCancelMeetingModal}
            />

            <AddToCalendarModal
                isOpen={isAddToCalendarModalOpen}
                meeting={meeting}
                otherUser={user}
                closeModal={closeCalendarModal}
            />
        </Track>
    );
};

const useStyles = makeStyles(
    theme => ({
        redColor: {
            color: theme.palette.primary[500],
        },
    }),
    { classNamePrefix: 'meetings-meeting-scheduled-item' }
);

export default ScheduledItem;
